import {
  collection,
  doc,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Notification } from '../types/friends';
import { removeUndefined, getTimestampMillis } from '../utils/formatUtils';

/**
 * Creates a notification for a user
 */
export const createNotification = async (
  userId: string,
  notification: Omit<Notification, 'id' | 'createdAt'>
): Promise<string> => {
  try {
    const notificationsRef = collection(db, 'notifications', userId, 'notifications');
    const cleaned = removeUndefined({
      ...notification,
      createdAt: Date.now(),
    });
    const docRef = await addDoc(notificationsRef, cleaned);
    return docRef.id;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

/**
 * Gets notifications for a user
 */
export const getNotifications = async (
  userId: string,
  unreadOnly: boolean = false
): Promise<Notification[]> => {
  try {
    const notificationsRef = collection(db, 'notifications', userId, 'notifications');
    let q = query(notificationsRef, orderBy('createdAt', 'desc'));

    if (unreadOnly) {
      q = query(notificationsRef, where('read', '==', false), orderBy('createdAt', 'desc'));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: getTimestampMillis(doc.data().createdAt),
    })) as Notification[];
  } catch (error) {
    console.error('Error getting notifications:', error);
    throw error;
  }
};

/**
 * Marks a notification as read
 */
export const markNotificationRead = async (
  userId: string,
  notificationId: string
): Promise<void> => {
  try {
    const notificationRef = doc(db, 'notifications', userId, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      read: true,
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Marks all notifications as read for a user
 */
export const markAllNotificationsRead = async (userId: string): Promise<void> => {
  try {
    const notificationsRef = collection(db, 'notifications', userId, 'notifications');
    const q = query(notificationsRef, where('read', '==', false));
    const snapshot = await getDocs(q);

    const batch = snapshot.docs.map((docSnap) =>
      updateDoc(doc(db, 'notifications', userId, 'notifications', docSnap.id), {
        read: true,
      })
    );

    await Promise.all(batch);
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

/**
 * Deletes a notification
 */
export const deleteNotification = async (
  userId: string,
  notificationId: string
): Promise<void> => {
  try {
    const notificationRef = doc(db, 'notifications', userId, 'notifications', notificationId);
    await deleteDoc(notificationRef);
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

/**
 * Deletes notifications for a specific cruise and optional type (e.g. itinerary-available when linked)
 */
export const deleteNotificationsForCruise = async (
  userId: string,
  cruiseId: string,
  type?: Notification['type']
): Promise<void> => {
  try {
    const notificationsRef = collection(db, 'notifications', userId, 'notifications');
    let q = query(notificationsRef, where('cruiseId', '==', cruiseId));
    if (type) {
      q = query(notificationsRef, where('cruiseId', '==', cruiseId), where('type', '==', type));
    }
    const snapshot = await getDocs(q);
    const deletePromises = snapshot.docs.map((docSnap) => deleteDoc(docSnap.ref));
    await Promise.all(deletePromises);
  } catch (error) {
    console.error('Error deleting notifications for cruise:', error);
  }
};


